/**
 * BasicDataTab — renders the "Datos básicos *" tab fields.
 *
 * Supports two layouts driven by the definition:
 *  1. Sectioned (`definition.sections` present): renders each section with an optional
 *     titled header (icon + label + divider line) followed by the section's fields grid.
 *  2. Flat (`definition.fields` present): backward-compatible flat grid, no section titles.
 *     `headerFields` (code, name, isActive) are rendered first in both layouts.
 *
 * In GLOBAL type with company context, wraps each field in OverridableFieldWrapper.
 *
 * @module components/form/BasicDataTab
 * @see FMPV-MVWC-E003-S001 I-005
 */
import React from 'react';
import { Globe } from 'lucide-react';
import type { MasterPatternViewDefinition, MasterPatternViewFieldDefinition } from '../../types/definition.types';
import type { MasterPatternViewCompany } from '../../types/service.types';
import { FieldRenderer } from './fields/FieldRenderer';
import { OverridableFieldWrapper } from './fields/OverridableFieldWrapper';
import { computeColSpanClass, inferInputType } from '../../engine/fieldEngine';

export interface BasicDataTabProps {
  definition: MasterPatternViewDefinition;
  values: Record<string, unknown>;
  errors: Record<string, string>;
  onChange: (field: string, value: unknown) => void;
  headerFields?: string[];
  readOnly?: boolean;
  /** 'company' when a specific company is selected; 'global' otherwise */
  fieldContext?: 'global' | 'company';
  isShowGlobalValuesEnabled?: boolean;
  globalValues?: Record<string, unknown>;
  onFieldRevert?: (fieldName: string, globalValue: unknown) => void;
  /** Current form mode — forwarded to renderFormField as context */
  formMode?: 'create' | 'edit' | 'view';
  /** This form's own company list — forwarded to a lookup field's inline "create new" so it can seed the child master's company selector instead of always defaulting to Global. */
  companies?: MasterPatternViewCompany[];
  /** This form's own currently active company (undefined/'global' when in global context) — same forwarding as `companies`. */
  activeCompanyId?: string | null;
}

const DEFAULT_HEADER_FIELDS = ['code', 'name', 'isActive'];

/** Renders a single field — either plain or wrapped in OverridableFieldWrapper. */
function renderField(
  field: MasterPatternViewFieldDefinition,
  props: BasicDataTabProps,
  showOverrides: boolean,
  values: Record<string, unknown>,
) {
  const { onChange, errors, readOnly = false, fieldContext, isShowGlobalValuesEnabled = false, globalValues = {}, onFieldRevert, formMode, companies, activeCompanyId } = props;

  const isDisabled = typeof field.disabled === 'function'
    ? field.disabled(values)
    : (field.disabled ?? false);

  const renderer = (
    <FieldRenderer
      key={field.fieldName}
      field={field}
      value={values[field.fieldName]}
      onChange={(v) => onChange(field.fieldName, v)}
      error={errors[field.fieldName]}
      disabled={isDisabled}
      readOnly={readOnly}
      allValues={values}
      formMode={formMode}
      setFormValue={onChange}
      companies={companies}
      activeCompanyId={activeCompanyId}
    />
  );

  if (!showOverrides) return renderer;

  return (
    <OverridableFieldWrapper
      key={field.fieldName}
      fieldName={field.fieldName}
      isOverridable={field.isOverridable ?? false}
      isCompanyOnly={field.isCompanyOnly ?? false}
      context={fieldContext!}
      globalValue={globalValues[field.fieldName]}
      isShowGlobalValuesEnabled={isShowGlobalValuesEnabled}
      onRevert={onFieldRevert ?? (() => {})}
      fieldType={field.inputType ?? inferInputType(field)}
      className={computeColSpanClass(field)}
    >
      {renderer}
    </OverridableFieldWrapper>
  );
}

export function BasicDataTab(props: BasicDataTabProps) {
  const {
    definition,
    values,
    headerFields = DEFAULT_HEADER_FIELDS,
    fieldContext,
    isShowGlobalValuesEnabled = false,
  } = props;

  const isGlobal = definition.type === 'GLOBAL';
  const showOverrides = isGlobal && !!fieldContext;
  const showNotice = showOverrides && fieldContext === 'company' && isShowGlobalValuesEnabled;

  const noticeBanner = showNotice ? (
    <div
      data-testid="global-fields-notice"
      className="mx-5 mt-3 mb-0 bg-[#eff8ff] border border-[#93d1fd] rounded-lg flex items-start gap-2 flex-shrink-0"
      style={{ padding: '9px 14px' }}
    >
      <Globe size={15} className="flex-shrink-0 text-[#0e79fd] mt-px" />
      <p className="text-[11px] text-[#1355b7] leading-[1.5]">
        Los campos deshabilitados corresponden a <strong>valores globales</strong> y solo pueden
        editarse desde el contexto Global.
      </p>
    </div>
  ) : null;

  // ── Sectioned layout ────────────────────────────────────────────────────────
  if (definition.sections) {
    return (
      <div data-testid="basic-data-tab">
        {noticeBanner}
        {definition.sections.map((section, idx) => {
          const SectionIcon = section.icon;
          return (
            <div key={section.label ?? idx}>
              {section.label && (
                <div className="flex items-center gap-2 px-5 pt-5">
                  {SectionIcon && (
                    <SectionIcon size={14} className="flex-shrink-0 text-[#1355b7]" />
                  )}
                  <span className="text-sm font-semibold text-[#1355b7] whitespace-nowrap">
                    {section.label}
                  </span>
                  <div className="flex-1 h-px bg-[#dde5ef] dark:bg-dark-border-primary" />
                </div>
              )}
              <div className="grid grid-cols-12 gap-x-4 gap-y-6 items-start px-5 pt-4 pb-2">
                {section.fields
                  .filter((f) => f.showInForm !== false)
                  .map((field) => renderField(field, props, showOverrides, values))}
              </div>
            </div>
          );
        })}
        {/* bottom padding to balance last section */}
        <div className="pb-4" />
      </div>
    );
  }

  // ── Flat layout (backward compat) ───────────────────────────────────────────
  const basicFields = (definition.fields ?? []).filter(
    (f) => f.showInForm === true && (f.formTab === undefined || f.formTab === 'basicData'),
  );

  // headerFields come first, then remaining basic fields
  const orderedFields = [
    ...headerFields
      .map((name) => basicFields.find((f) => f.fieldName === name))
      .filter(Boolean),
    ...basicFields.filter((f) => !headerFields.includes(f.fieldName)),
  ] as typeof basicFields;

  return (
    <div data-testid="basic-data-tab">
      {noticeBanner}
      <div className="grid grid-cols-12 gap-x-4 gap-y-6 items-start px-5 pt-4 pb-6">
        {orderedFields.map((field) => renderField(field, props, showOverrides, values))}
      </div>
    </div>
  );
}
